import {DeviceConnectProvide} from "@hybird/core/device/connect";
import mqtt from "mqtt";
import {
  DeviceEvent
} from "@hybird/core/device/protocol";
import {config} from "../config";
import {WebError} from "@hybird/core/error";
import {deviceError} from "@hybird/device/deviceError";
import service from "@hybird/core/request";
import url from "@hybird/device/request";
import {
  DefaultDeviceProtocol,
  DefaultDeviceProtocolBeginSeal,
  DefaultDeviceProtocolOperation
} from "@hybird/device/core";

class MqttDeviceConnectProvide extends DeviceConnectProvide {
  _connectForce = true;
  _end = false;

  async close(force = true) {
    await super.close();
    this._end = true;
    return new Promise((resolve, reject) => {
      if (this._client) {
        this._client?.end(force, (err) => {
          if (err) {
            reject(WebError.code(deviceError.CLOSE_ERROR, {
              error: err
            }));
          } else {
            resolve();
          }
        });
      } else {
        resolve();
      }
    });

  }

  /**
   * @type{ MqttClient }
   * @private
   */
  _client;

  get connectType() {
    return 2;
  }

  isSupport(connectType, config) {
    return connectType === 2;
  }

  connect({
            connectForce = true,
            onConnect,
            onConnected,
            onReconnect,
            onDisconnect,
            onDisconnected,
            onError,
            timeout,
            config
          } = {}) {
    return this.connectMqtt(connectForce, {
      onConnect, onConnected, onReconnect, onDisconnect, onDisconnected, onError, timeout, config
    });
  }

  connectMqtt(connectForce = false, {
    onConnect, onConnected, onReconnect, onDisconnect, onDisconnected, onError, timeout = 10, config
  } = {}) {
    this._connectForce = connectForce;

    return new Promise((resolve, reject) => {
      const timeoutConnect = () => {
        setTimeout(() => {
          if (!this._client.connected) {
            reject(WebError.code(deviceError.NOT_CONNECT_DEVICE));
          }
        }, timeout * 1000);
      };

      //如果当前存在, 并正在重连的情况, 直接返回当前的client
      if (this._client) {
        if (config.mqtt.clientId === this._client.options.clientId) {
          if (this._client.connected) {
            resolve(this);
            return;
          } else {
            if (this._connectForce || this._client.reconnecting) {
              this._client.on("connect", () => {
                resolve(this);
              });
              return;
            }
          }
        }

        //强制结束, 防止重连
        this._client.end(true);
      }

      timeoutConnect();

      const param = Object.assign({
        keepalive: 5, clean: true
      }, config.mqtt);

      if (!param.clientId || !param.password || !param.username) {
        throw WebError.code(deviceError.CONNECT_ILLEGAL_ARGUMENT);
      }

      onConnect?.call(this);
      config?.onConnect?.call(this, this);
      this._client = mqtt.connect(config.mqtt.url, param);

      this._client.on("message", (topic, payload, packet) => {
        const replyData = JSON.parse(payload.toString());
        console.log("message", topic, JSON.stringify(replyData));
        this._onMessage(topic, replyData);
      });

      this._client.on("connect", () => {
        resolve(this);
        config?.onConnected?.call(this, this);
        onConnected?.call(this);
      });

      this._client.on("reconnect", (err) => {
        config?.onReconnect?.call(this, 1, err, this);
        onReconnect?.call(this, err);
      });

      this._client.on("disconnect", (err) => {
        onDisconnected?.call(this, err);
      });

      this._client.on("end", (err) => {
        if (this._connectForce && !this._end) {
          config?.onReconnect?.call(this, 2, err, this);
          this.connectMqtt(connectForce, {config}).then((client) => {
            resolve(this);
          });
        } else {
          reject(WebError.code(deviceError.CONNECT_ERROR, {}));
        }
      });

      this._client.on("error", (error) => {
        config.onError?.call(this, error, this);
        onError?.call(this);
      });
    });
  }

  get connected() {
    return this._client != null ? this._client.connected : false;
  }

  _subscribe(topic, {success, message, complete, fail} = {}) {
    return new Promise((resolve, reject) => {
      if (!topic.startsWith("/")) {
        console.warn("topic 地址不对", topic);
        reject(WebError.code(deviceError.SUBSCRIBE_ERROR));
        return
      }

      this._client.subscribe(topic, {qos: 0}, async (err) => {
        if (err) {
          fail?.call(this, err);
          reject(err);
        } else {
          await success?.call(this, err);
          resolve(this);
        }
        complete?.call(this, null, err);
      });
    });
  }

  _unsubscribe(topic, {success, error, complete, fail} = {}) {
    super._unsubscribe(topic, {success, error, complete, fail});

    return new Promise((resolve, reject) => {
      this._client.unsubscribe(topic, async (err) => {
        if (err) {
          fail?.call(this, err);
          reject(err);
        } else {
          await success?.call(this);
          resolve();
        }
        complete?.call(this, null, err);
      });
    });
  }

  async _publish(topic, params, {id, success, complete, fail} = {}) {
    await super._publish(topic, params, {success, complete, fail});

    const records = {
      id: id ? id : this.id, version: "1.0", params: params ?? {}, time: Date.now()
    };

    return new Promise((resolve, reject) => {
      console.log(this.toString(), "publish", topic, JSON.stringify(records));
      if (!topic.startsWith("/")) {
        console.warn("topic 地址不对", topic);
        reject(WebError.code(deviceError.WRITE_ERROR));
      }

      this._client.publish(topic, JSON.stringify(records), async (err) => {
        if (err) {
          fail?.call(this, err);
          reject(err);
        } else {
          await success?.call(this);
          resolve(records);
        }
        complete?.call(this, null, err);
      });
    });
  }
}

/**
 * @abstract
 */
class MqttDeviceProtocol extends DefaultDeviceProtocol {
  constructor(deviceProvide, deviceInfo, deviceProtocolOption = {}) {
    super(deviceProvide, deviceInfo, deviceProtocolOption);
    this.send = new MqttSend(this);
  }

  _version = 2;

  get version() {
    return this._version;
  }

  set version(version) {
    this._version = version;
  }

  get connectType() {
    return 2;
  }

  async canConnect() {
    if (!this.online) {
      await this.deviceStatus();
    }

    return this.online;
  }

  set deviceInfo(deviceInfo) {
    super.deviceInfo = deviceInfo;
    this.send?.init(deviceInfo);
  }

  get deviceInfo() {
    return super.deviceInfo;
  }

  topic(name, {reply = false, device = false, type = 1} = {}) {
    if (type === 3) {
      return `/sys/yda/${this.deviceInfo.number}/online`;
    } else if (type === 4) {
      return `/sys/yda/${this.deviceInfo.number}/offline`;
    } else if (type === 5) {
      return `/${device ? "sys" : "cell"}/yda/${this.deviceInfo.number}/ota/firmware/${name}${reply ? "_reply" : ""}`;
    }

    return `/${device ? "sys" : "cell"}/yda/${this.deviceInfo.number}/${type === 1 ? "service" : "event"}/${name}/${type === 1 ? "invoke" : "post"}${reply ? "_reply" : ""}`;
  }

  async service(name, params = {}, {
    type = 1, success, complete, message, fail, timeout, topic, retry = true, noReply
  } = {}) {
    if (this.connectType !== this.deviceConnectProvide.connectType) {
      console.log("异常发送情况");
      throw WebError.code(deviceError.WRITE_ERROR, "当前发送数据的连接方式与协议对象不一致");
    }

    const getTopic = this.topic(name, {
      reply: true, device: true, type
    });
    const putTopic = this.topic(name, {
      type, device: name === "fingerprint_entering"
    });

    let publishAndResult = false;

    let onTimeout = () => {
    };

    const timeoutCallback = setTimeout(() => {
      publishAndResult = true;
      onTimeout();
    }, timeout * 1000);

    if (noReply) {
      return this.deviceConnectProvide.publish(putTopic, params, {
        success, complete, message, fail, timeout
      });
    }

    const exec = async () => {
      return this.deviceConnectProvide.publishAndResult(getTopic, putTopic, params, {
        success, complete, message, fail, timeout
      }).then(data => {
        topic?.call(this, {getTopic, putTopic});
        publishAndResult = true;

        if (data.success === false) {
          throw this.exception({data});
        }

        return data;
      }).catch(e => {
        if (!deviceError.isError(e, deviceError.SUBSCRIBE_ONE_READ_TIMEOUT) || this.online || publishAndResult) {
          throw e;
        }

        return new Promise((resolve, reject) => {
          onTimeout = () => {
            this.off(DeviceEvent.ONLINE, online);
            reject(e);
          }

          const online = async () => {
            if (!this.connected) {
              return;
            }

            if (!publishAndResult && retry) {
              const data = await exec();
              resolve(data);
            }
          }

          this.once(DeviceEvent.ONLINE, online);
        })
      }).finally(() => {
        clearTimeout(timeoutCallback);
      })
    };

    return exec();
  }

  /**
   *
   * @param name
   * @param event
   * @param type
   * @param one
   * @param reply
   * @param device
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @param topic
   * @return {Promise<String|{code: Number, success: boolean, data: Object}>}
   */
  async listen(name, event = true, {
    type, one = false, reply = false, device = true, success, complete, message, fail, timeout, topic
  } = {}) {
    const getTopic = this.topic(name, {
      reply: reply, device: device, type: type ? type : event ? 2 : 1
    });

    let result;
    let index;

    if (one) {
      result = this.deviceConnectProvide.subscribeOne(getTopic, {
        success: async (i) => {
          index = i;
          await success?.call(this, i);
        }, complete, message, fail, timeout
      });
    } else {
      index = result = await this.deviceConnectProvide.subscribe(getTopic, {
        success, complete, message, fail
      });
    }
    if (index) {
      if (topic) {
        topic?.call(this, {getTopic, index});
      }
    }

    return result;
  }

  async _connect({success, complete, message, fail, timeout} = {}) {
    let gps = {
      lon: config.gps.lon, lat: config.gps.lat
    };

    return this.service("user_login", {
      model: 2,
      staffId: this.deviceInfo.staffId,
      sessionId: this.deviceInfo.sessionId,
      deviceType: config.deviceType,
      staffName: this.deviceInfo.userName,
      gps: gps,
      version: 3,
    }, {
      success, complete, message, fail, timeout
    }).then(async data => {
      this._connected = true;
      await this.send.init();
      this.userLogin = true;

      if (data.data.version) {
        this._version = 3;
      } else {
        this._version = 2;
      }

      return data;
    }).catch(e => {
      if (deviceError.isError(e, deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS)) {
        if (e.data.code === 100004) {
          throw WebError.code(deviceError.DEVICE_USE_NOT_CONNECT, {error: e});
        }
      }

      throw e;
    });
  }

  async _disconnect({type, logout, success, complete, message, fail, timeout} = {}) {
    await this.endSeal({force: true, type, logout});

    if (type !== 3) {
      await this.service("user_logout", {
        model: 2, staffId: this.deviceInfo.staffId, sessionId: this.deviceInfo.sessionId, deviceType: config.deviceType
      }, {
        success, complete, message, fail, timeout
      }).finally(() => {
        this.send.unsubscribe();
      })
    }
  }

  async _precord() {
    if (this.version < 4) {
      return super._precord();
    }

    let timeoutCallback;
    let dataList = [];
    let totalSize = 0;
    let timeout = 20;

    const getDate = (size) => {
      let length = 0;

      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        let pageIndex = 0;
        let pageSize = 0;
        const cacheTopic = {}

        timeoutCallback = setTimeout(() => {
          reject(WebError.code(deviceError.PRECORD_TIMEOUT));
        }, timeout * 1000);

        await this.send.listen("privilege_data", {
          cacheTopic: cacheTopic,
          message: async (message) => {
            try {

              const params = message.params;
              totalSize = parseInt(params[1]);
              pageIndex = parseInt(params[2]);
              pageSize = parseInt(params[3]);
              const data = JSON.parse(params[4]);
              // dataList.push(...data);
              if (pageSize > 0) {
                const dataWithNumber = data.map(item => ({
                  ...item,
                  number: this.deviceInfo.number
                }));
                length += dataWithNumber.length;

                await service.post(url.documentPrivilegeUpload, dataList).then((data) => {
                  return this.send.service("privilege_data_result", [1]);
                }).catch(async e => {
                  await this.send.service("privilege_data_result", [2]);

                  throw e;
                });

                this.emit(DeviceEvent.SYNC_STATUS, {
                  progress: (totalSize === 0 ? 1 : length / totalSize) * 99 / 100,
                  type: 2,
                  data: dataWithNumber
                });
              }


              if ((size > totalSize ? totalSize : size) === length) {
                await this.send.unsubscribe(cacheTopic);
                resolve(totalSize <= dataList.length);
                dataList = []

                //清理定时器
                clearTimeout(timeoutCallback);
              }
            } catch (e) {
              await this.send.unsubscribe(cacheTopic);
              reject(WebError.error(e, deviceError.PRECORD_ERROR))
            }
          }
        }).catch(e => {
          reject(WebError.error(e, deviceError.PRECORD_ERROR))
        });
      });
    }
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const size = 100;
      const data = getDate(size);
      await this.send.service("privilege_data", [size], {noReply: true});

      const done = await data;

      if (done) {
        break;
      }
    }
    this.emit(DeviceEvent.SYNC_STATUS, {
      progress: 1,
      type: 2,
      data: dataList
    });
  }

  async _setDeviceConfig({config, success, complete, message, fail, timeout}) {
    // return this.service("device_config_set", config, {
    //   success, complete, message, fail, timeout
    // })
  }
}

const propertyList = {}

class MqttSend {
  /**
   * @type {MqttDeviceProtocol}
   */
  deviceProtocol;

  /**
   *
   * @type {{String: function}}
   */
  topics = {};

  network = 1;

  _listenTopic = {};

  number;


  constructor(deviceProtocol, listen = true) {
    this._listen = listen;
    this.deviceProtocol = deviceProtocol;

    setTimeout(() => {
      this.init().finally();
    }, 200);
  }

  get property() {
    return propertyList[this.deviceProtocol.deviceInfo.number];
  }

  async onDisconnect() {
    if (this._listen) {
      this.number = this.deviceProtocol.deviceInfo.number;

      await this.listen("property", true, {
        cacheTopic: this._listenTopic, message: data => {
          this.deviceProtocol.emit(DeviceEvent.PROPERTY, data);

          if (data.params.network === 2) {
            this.network = data.params.network;
          }

          propertyList[this.deviceProtocol.deviceInfo.number] = data.params;
        }
      });

      await this.listen("online", true, {
        cacheTopic: this._listenTopic, type: 3, message: data => {
          this.deviceProtocol.emit(DeviceEvent.ONLINE, data);
          this.deviceProtocol.online = true;
        }
      });

      await this.listen("offline", true, {
        cacheTopic: this._listenTopic, type: 4, message: data => {
          this.deviceProtocol.emit(DeviceEvent.OFFLINE, data);
          this.deviceProtocol.online = false;
          this.disconnect();
        }
      });

      await this.listen("user_logout", true, {
        cacheTopic: this._listenTopic, message: data => {
          if (data?.params?.type) {
            this.disconnect(false, 7 + data?.params?.type);
          } else {
            this.disconnect();
          }
        }
      });

      await this.listen("user_logout", false, {
        cacheTopic: this._listenTopic, message: data => {
          this.disconnect();
        }
      });

      this.deviceProtocol.on(DeviceEvent.USER_LOGIN, () => {
        this.createKeepAlive();
      })

      this.deviceProtocol.on(DeviceEvent.USER_LOGOUT, () => {
        this.disconnect();
      })
    }
  }

  async init(deviceInfo = null) {
    if (deviceInfo) {
      if (this._init) {
        if (this.number === deviceInfo.number) {
          return;
        }

        await this.unsubscribe(this._listenTopic);
        this._listenTopic = {};
      }
    }

    //已经初始化过
    if (this._init) {
      return;
    }

    console.log("MqttSend init", this.deviceProtocol.connected, this._init, this.deviceProtocol.deviceInfo);
    if (this.deviceProtocol.connected || (this.deviceProtocol.deviceInfo.property && this.deviceProtocol.deviceConnectProvide.connected)) {
      this._init = true;
      await this.onDisconnect();
    } else {
      const onConnect = ({connectType}) => {
        if (connectType === 2) {
          if (!this._init) {
            this._init = true;
            this.onDisconnect();
          }

          this.deviceProtocol.removeListener(DeviceEvent.CONNECTED, onConnect);
        }
      };
      this.deviceProtocol.on(DeviceEvent.CONNECTED, onConnect)
    }
  }

  disconnect(clear = false, type = 3) {
    if (this.clearKeepAlive) {
      this.clearKeepAlive();

      if (this.deviceProtocol.deviceProtocolOption["onUserLogout"] instanceof Function) {
        this.deviceProtocol.deviceProtocolOption["onUserLogout"]({force: true, logout: true, type: 3});
      }
    }

    this.unsubscribe(this._listenTopic).finally(() => {
      this._listenTopic = {};
      this._init = false;

      if (!clear) {
        return this.deviceProtocol.disconnect({force: true, logout: true, type: 3, connectType: 2});
      }
    });

  }

  createKeepAlive() {
    this.keepAliveCount = 0

    if (!this.keepAliveMethod) {
      const clear = async (type = 4) => {
        await this.disconnect(false, type);
      };

      this.clearKeepAlive = () => {
        this.clearKeepAlive = null;

        if (this.keepAliveMethod) {
          clearInterval(this.keepAliveMethod, true);
          this.deviceProtocol.removeListener(DeviceEvent.PROPERTY, listenProperty);

          this.keepAliveMethod = null;
          this.keepAlive({end: true});
        }
      };

      this.deviceProtocol.once(DeviceEvent.OFFLINE, clear);
      this.deviceProtocol.once(DeviceEvent.DISCONNECTED, clear);

      // TODO 暂时不处理异常断开连接的问题, 等待重连
      // this.deviceProtocol.once(DeviceEvent.ERROR, clear);

      this.deviceProtocol.once(DeviceEvent.END_SEAL, () => {
        // this.deviceProtocol.removeListener(DeviceEvent.ERROR, clear);
        this.deviceProtocol.removeListener(DeviceEvent.OFFLINE, clear);
        this.deviceProtocol.removeListener(DeviceEvent.DISCONNECTED, clear);
        this.deviceProtocol.removeListener(DeviceEvent.END_SEAL, clear);
      });

      let time;
      const keepaliveTime = 10 * 1000;

      const listenProperty = async () => {
        let currTime = new Date().getTime();
        if (time && currTime - time < (keepaliveTime - 1000)) {
          return;
        }

        time = currTime;

        if (this.keepAliveCount > 2) {
          console.error("三次心跳异常");
          await clear(7);
          return;
        }

        try {
          this.keepAliveCount = this.keepAliveCount ? (this.keepAliveCount + 1) : 1;
          await this.keepAlive();

          this.keepAliveCount = 0;
        } catch (e) {
          if (deviceError.isError(deviceError.PUBLISH_AND_RESULT_NOT_SUCCESS)) {
            console.error("发送心跳异常退出登录", e);
            await clear(7);
          } else {
            console.error("发送心跳异常", e);
          }
        }
      }

      //在后台运行情况, 定时器会被暂停, 使用数据传输激活心跳
      this.deviceProtocol.on(DeviceEvent.PROPERTY, listenProperty);
      this.keepAliveMethod = setInterval(listenProperty, keepaliveTime, true);
    }
  }

  /**
   *
   * @param name
   * @param params
   * @param type
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @param retry
   * @return {Promise<*>}
   */
  async service(name, params = {}, {
    type, success, complete, message, fail, timeout, retry, noReply
  } = {}) {
    return this.deviceProtocol.service(name, params, {
      type, success, complete, message, fail, timeout, retry, noReply
    });
  }

  /**
   *
   * @param name
   * @param event
   * @param type
   * @param cacheTopic{Boolean|{}}
   * @param one
   * @param reply
   * @param device
   * @param success
   * @param complete
   * @param message
   * @param fail
   * @param timeout
   * @return {Promise<String|{code: Number, success: boolean, data: Object}>}
   */
  async listen(name, event = true, {
    type, cacheTopic = true, one = false, reply = false, device = true, success, complete, message, fail, timeout
  } = {}) {
    return this.deviceProtocol.listen(name, event, {
      type, one, reply, device, success, complete, message, fail, timeout, topic: ({getTopic, index}) => {
        if (cacheTopic) {
          if (cacheTopic === true && index && !this.topics[index]) {
            this.topics[index] = getTopic;
          }

          if (cacheTopic !== true) {
            cacheTopic[index] = getTopic;
          }
        }
      }
    });
  }

  async unsubscribe(topics) {
    let clearTopics = [];
    if (topics) {
      if (typeof topics === "string") {
        clearTopics.push(topics);
      } else {
        clearTopics = topics;
      }
    } else {
      clearTopics = this.topics;
    }

    for (const index in clearTopics) {
      await this.deviceProtocol.deviceConnectProvide.unsubscribe(clearTopics[index], {index});

      if (this.topics[index]) {
        delete this.topics[index];
      }
    }
  }

  async keepAlive({end, documentId, lock, success, complete, message, fail, timeout} = {}) {
    return this.service("keep_alive", {
      sessionId: this.deviceProtocol.deviceInfo.sessionId, end
    }, {success, complete, message, fail, timeout});
  }

  async remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
    return this.service("remoteLock", {
      documentId, lock
    }, {success, complete, message, fail, timeout});
  }

  async cancelOperation({type, success, complete, message, fail, timeout} = {}) {
    // if (type === 1 && this.deviceProtocol.deviceInfo.leftTimes <= 0 && this.deviceProtocol.deviceInfo.remote !== DeviceBeginSealType.remote && !this.deviceProtocol.deviceProtocolBeginSeal?.remoteLockStatus) {
    //   return;
    // }
    //
    // const noReply = type === 1 && this.deviceProtocol.deviceInfo.leftTimes <= 0 && this.deviceProtocol.deviceInfo.remote === DeviceBeginSealType.remote

    return this.service("cancel_operation", {
      type: type
    }, {success, complete, message, fail, timeout, noReply: true});
  }
}

class MqttDeviceProtocolBeginSeal extends DefaultDeviceProtocolBeginSeal {

  /**
   *
   * @param deviceInfo
   * @param deviceProtocol{MqttDeviceProtocol}
   * @param option
   */
  constructor(deviceInfo, deviceProtocol, option) {
    super(deviceInfo, deviceProtocol, option);

    /**
     *
     * @type {MqttSend}
     */
    this.send = deviceProtocol.send;
  }

  async _examineResult(documentRemoteId, state, {mode, type, success, complete, message, fail, timeout}) {
    return this.send.service("examine_result", {
      documentRemoteId, state, mode, documentId: this.option.documentId, type
    }, {
      success, complete, message, fail, timeout
    }).then(data => {
      return data;
    }).catch(e => {
      throw e;
    });
  }

  async _equipmentLock({
                         sendEquipmentLock, success, complete, message, fail, timeout = -1
                       } = {}) {
    const data = await sendEquipmentLock?.call(this);

    const documentRemoteId = data.documentRemoteId;

    let subscribeIndex;
    let clear = false;
    const moveEvent = this.deviceProtocol.topic("equipment_move_event", {
      reply: false, device: true, type: 2
    });

    const onDocumentStateUploading = params => {
      params.documentRemoteId = documentRemoteId;
      this.deviceProtocol.emit(DeviceEvent.DOCUMENT_REMOTE_STATE_UPLOADING, params);
    };

    /**
     *
     * @param type 1:正常结束  2:移动取消  3:app取消
     * @return {Promise<void>}
     */
    const clearMoveEvent = async (type = 3) => {
      if (subscribeIndex && !clear) {
        clear = true;
        this.deviceProtocol.deviceConnectProvide.unsubscribe(moveEvent, {index: subscribeIndex});
        if (type === 2) {
          this.deviceProtocol.emit(DeviceEvent.EQUIPMENT_MOVE, {documentRemoteId});
        } else if (type === 3) {
          this._remoteUnlock(documentRemoteId, type).catch(e => {
            console.warn("远程用印取消失败", e);
            return null;
          })
        }

        if (type !== 1) {
          this.deviceProtocol.removeListener(DeviceEvent.DOCUMENT_STATE_UPLOADING, onDocumentStateUploading);
        }

        this.deviceProtocol.emit(DeviceEvent.EQUIPMENT_UNLOCK, {documentRemoteId, type: type});
      }
    };

    this.deviceProtocol.deviceConnectProvide.subscribeOne(moveEvent, {
      success: index => {
        subscribeIndex = index;
      }, filter: params => {
        return documentRemoteId === params?.data?.documentRemoteId;
      }, message: params => {
        clearMoveEvent(2);
      }, timeout
    }).finally();

    this.once(DeviceEvent.DOCUMENT_STATE_UPLOADING, onDocumentStateUploading);

    await this.send.listen(DeviceEvent.EQUIPMENT_LOCK, {
      one: true, reply: false, type: 1, success, complete, message, fail, timeout
    });

    const index = await this.send.listen("examine_result", false, {
      reply: false, type: 1, success, complete, message: async (data) => {
        if (documentRemoteId === data.params.documentRemoteId || this.deviceInfo.documentId === data.params.documentId) {
          await clearMoveEvent(data.params.state === 1 ? 1 : 4);
          this._context.mode = this._mode = 1;
          this._context.isExamine = true;
          // await this._examineResult(documentRemoteId, data.params.state);
          this.deviceProtocol.deviceConnectProvide.unsubscribe("examine_result", index);
          message?.call(this);
        }
      }, fail, timeout
    });

    return {
      documentRemoteId, cancel: clearMoveEvent
    };
  }

  async _fingerprintIdentify({success, complete, message, fail, timeout = -1} = {}) {
    return new Promise((resolve, reject) => {
      this.send.listen("fingerprint_identify_result", true, {
        success, complete, timeout, message: data => {
          let result;
          if (!((data?.params?.sealId === this.option.sealId || data?.params?.sealid === this.option.sealId) && data?.params?.documentId === this.option.documentId)) {
            console.warn("fingerprint_identify_result", "当前设备与下发的数据不一致, 请检查deviceInfo", JSON.stringify(this.option), JSON.stringify(data));
          }

          this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_IDENTIFY, result = {
            documentId: this.option.documentId,
            sealId: this.option.sealId,
            state: data.params?.state,
            success: data.params?.state === 1
          });

          message?.call(this, result);

          if (data.params?.state === 1) {
            resolve(result);
          }
        }, fail: (err) => {
          fail?.call(this, err);
          reject(err);
        }
      });
    });
  }

  async _beginSeal({success, complete, message, fail, timeout} = {}) {
    const params = {
      operationId: this.deviceInfo.operationId,
      authentication: this.option.authentication,
      documentId: this.option.documentId,
      sealId: this.option.sealId,
      leftTimes: this.option.leftTimes,
      positions: this.option.position,
      remote: this.option.remote !== 2 && this.option.sealAuthentication === 1 ? 4 : this.option.remote,
      pictureType: this.option.pictureType,
      businessKey: this.option.businessKey,
      staffId: this.option.staffId,
      infrared: this.option.infrared,
      equipmentRecVideoAllType: this.deviceInfo.equipmentRecVideoAllType,
      everyType: this.deviceInfo.everyType,
      riskType: this.deviceInfo.riskType,
      timeDivision: this.deviceInfo.timeDivision,
      parseBeforeStamping: this.deviceInfo.parseBeforeStamping,
      snapIntervalTx: this.deviceInfo.snapIntervalTx,
    }

    if (this.deviceProtocol.version >= 3) {
      params.sealAuthentication = this.deviceInfo.deviceSealAuthentication
    }

    return this.send.service("begin_seal", params, {
      success, complete, message, fail, timeout
    }).catch(e => {
      this.deviceProtocol.disconnect();
      throw e;
    });
  }

  async _remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
    return this.send.remoteLock({
      documentId, lock, success, complete, message, fail, timeout
    });
  }

  async _endSeal({success, complete, message, fail, timeout} = {}) {
    return this.send.cancelOperation({
      type: 1, success, complete, message, fail, timeout
    }).then(data => {
      this.send.unsubscribe();

      return data;
    });
  }

  async _sealStretch({out = true, urgent, remoteLock = 2, success, complete, message, fail, timeout} = {}) {
    if (out) {
      return this.send.service("seal_stretch_out", {
        urgent, remoteLock
      }, {success, complete, message, fail, timeout}).then(data => {
        if (data.success) {
          this.deviceProtocol.emit(DeviceEvent.SEAL_STRETCH, {out, urgent});
        }

        return data;
      });
    } else {
      return this.send.service("seal_retraction", {
        remoteLock
      }, {success, complete, message, fail, timeout}).then(data => {
        if (data.success) {
          this.deviceProtocol.emit(DeviceEvent.SEAL_STRETCH, {out, urgent});
        }

        return data;
      });
    }
  }

  async _onSealEvent({success, complete, message, fail, timeout} = {}) {
    try {
      await this.send.listen("document_state_uploading_event", true, {
        message: data => {
          if (data.params.documentId == this.deviceInfo.documentId && data.params.staffId == this.deviceInfo.staffId) {
            //mqtt存在离线上报的情况
            if (this.option.currStartLeftTimes < data.params.leftTimes) {
              console.warn(`上报离线图片数据, 开始: ${this.option.currStartLeftTimes}, 现数据：${this.deviceInfo.leftTimes}, 原数据：${data.params.leftTimes}`)
              return;
            }

            if (this.deviceInfo.modelType === 1) {
              this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 1});
            }

            this.option.leftTimes = data.params.leftTimes;
            this.deviceInfo.leftTimes = data.params.leftTimes;

            this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 2});
            this.deviceProtocol.emit(DeviceEvent.DOCUMENT_STATE_UPLOADING, data.params);

            message?.call(this, data);
          }
        }, fail, timeout
      });

      await this.send.listen("report_dev_error", true, {
        message: data => {
          this.deviceProtocol.emit(DeviceEvent.REPORT_DEV_ERROR, data.params);
          this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 4});

          //TODO 按业务需求看需不需要重置, 或者直接取消用印
          message?.call(this, data);
        }, fail, timeout
      });

      await this.send.listen("abnormal_warning_event", true, {
        message: data => {
          this.deviceProtocol.emit(DeviceEvent.ABNORMAL_WARNING, data.params);

          //和枚举类保持一致的错误类型
          switch (data.params?.warningType) {
            case 1:
              this.deviceProtocol.emit(DeviceEvent.ABNORMAL_WARNING, {
                type: 4, time: new Date(data.params.warningTime)
              });
          }

          this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 4});

          //TODO 按业务需求看需不需要重置, 或者直接取消用印
          message?.call(this, data);
        }, fail, timeout
      });

      await this.send.listen("stop_privilege_seal", true, {
        message: data => {
          if (this.option.documentId === data.params?.documentId && data.params?.staffIds?.find(staffId => staffId === this.option.staffId)) {
            this.deviceProtocol.emit(DeviceEvent.STOP_PRIVILEGE_SEAL, data.params);
            this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 5});
            this.deviceProtocol.endSeal({force: true, type: 5});
          }

          message?.call(this, data.params);
        }, fail, timeout
      });


      await this.send.listen("click", true, {
        message: data => {
          this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 1});

          message?.call(this, data);
        }, fail, timeout
      });

      await this.send.listen("action_end", true, {
        message: data => {
          this.deviceProtocol.emit(DeviceEvent.SEAL_STATUS, {status: 3});
          message?.call(this, data);

          if (this._mode === 1 && this._context.isExamine) {
            this.contextReset(true);
          }
        }, fail, timeout
      });

      await this.send.listen("document_file_uploadMsg_event", false, {
        message: data => {
          console.log("监听到盖印数据", data);

          if (data.params.documentId == this.deviceInfo.documentId && data.params.staffId == this.deviceInfo.staffId) {
            if (this.option.currStartLeftTimes < data.params.leftTimes) {
              console.warn(`上报离线图片数据, 开始: ${this.option.currStartLeftTimes}, 现数据：${this.deviceInfo.leftTimes}, 原数据：${data.params.leftTimes}`)
              return;
            }
            this.deviceProtocol.emit(DeviceEvent.DOCUMENT_FILE_UPLOAD, data);
          }
        }
      });
      await success?.call(this);
      complete?.call(this);
    } catch (e) {
      complete?.call(this, null, e);
      throw WebError.code(deviceError.SYSTEM_ERROR, {
        error: e
      });
    }
  }

  async _clickResult({status, desc, success, complete, message, fail, timeout}) {
    return this.send.service("click_result", {
      status, desc
    }, {success, complete, message, fail, timeout});
  }
}


class MqttDeviceProtocolOperation extends DefaultDeviceProtocolOperation {

  constructor(deviceInfo, deviceProtocol) {
    super(deviceInfo, deviceProtocol);

    /**
     *
     * @type {MqttSend}
     */
    this.send = deviceProtocol.send;
  }

  async _fingerprintEntering({staffId, success, complete, message, fail, timeout} = {}) {
    const result = await service.put(url.fingerprint, {
      number: this.deviceInfo.number, staffId: staffId, remote: 1,
    });

    const {type, position, auth} = result.data;
    const _onFingerprint = await this._onFingerprint(type, position, auth);

    return {
      type, position, auth, cancel: ({success, complete, message, fail, timeout} = {}) => {
        _onFingerprint?.cancel();
        return this._cancelOperation({type: 3, success, complete, message, fail, timeout});
      }
    };
  }

  async _fingerprintRemove({staffId, success, complete, message, fail, timeout} = {}) {
    const result = await service.getFrom(url.fingerprintRemove, {
      staffId: staffId,
    });

    return {
      type: 2
    }
  }

  async _onFingerprint(type, position, auth) {
    const topics = [];
    const result = {
      cancel: () => {
        this.send.unsubscribe(topics);
        this.send.cancelOperation({type: 3});
      }
    };

    await this.send.listen("fingerprint_type", true, {
      cacheTopic: topics, message: data => {
        if (data.success || this.deviceProtocol.version === 2) {
          this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_TYPE, {step: 3, type, position, auth, data});
        } else {
          this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_TYPE, {
            step: -1,
            type,
            position,
            auth,
            data,
            error: this.deviceProtocol.exception({data})
          });
          this.send.unsubscribe(topics);
        }
      }
    });

    await this.send.listen("fingerprint_verify", true, {
      cacheTopic: topics, message: data => {
        this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_VERIFY, data);
      }
    });

    await this.send.listen("fingerprint_operation_state", true, {
      cacheTopic: topics, message: data => {
        this.send.unsubscribe(topics);

        const params = {
          success: data.params?.status === 1, data: {
            ...data.params
          }
        }

        this.deviceProtocol.emit(DeviceEvent.FINGERPRINT_FINISH, params);
        // 录入成功时不cancel
        const {type, status} = params.data || {}
        if (type === 1 && status === 1) return
        result.cancel();
      }
    });
    return result;
  }

  async _withInk({success, complete, message, fail, timeout} = {}) {
    return this.send.service("with_ink", {
      type: 1
    }, {success, complete, message, fail, timeout});
  }

  async _beginInk({success, complete, message, fail, timeout} = {}) {
    return this.send.service("begin_ink", {
      type: 1
    }, {success, complete, message, fail, timeout});
  }

  async _beginInkResult({success, complete, message, fail, timeout} = {}) {
    return this.send.service("begin_ink_result", {
      type: 1
    }, {success, complete, message, fail, timeout});
  }

  async _cancelOperation({type, success, complete, message, fail, timeout} = {}) {
    return this.send.cancelOperation({
      type, success, complete, message, fail, timeout
    });
  }

  async _changeSeal({sealId, motion, success, complete, message, fail, timeout} = {}) {
    return this.send.service("change_seal", {
      sealId, motion
    }, {success, complete, message, fail, timeout});
  }

  async _changeSealFinish({sealId, motion, success, complete, message, fail, timeout} = {}) {
    return this.send.service("change_seal_finish", {
      sealId, motion
    }, {success, complete, message, fail, timeout});
  }

  async _deviceMaintain({success, complete, message, fail, timeout} = {}) {
    return this.send.service("device_maintain", {}, {success, complete, message, fail, timeout});
  }

  async _remoteLock({documentId, lock, success, complete, message, fail, timeout} = {}) {
    return this.send.remoteLock({documentId, lock, success, complete, message, fail, timeout});
  }

  async _firmwareUpgrade({type = 1, data, success, complete, message, fail, timeout = 60 * 5} = {}) {
    const progress = {
      step: 1, progress: 0
    }

    try {
      const time = 100;
      const totalTime = 5000;

      this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, progress);

      const index = setInterval(() => {
        if (progress.step >= 3 || progress.step < 0) {
          clearInterval(index);
        }

        if (progress.progress < 100) {
          if (progress.progress < 100) {
            progress.progress += 100 / (totalTime / time)
          }
          this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, progress);
        }
      }, time)

      const result = await this.send.service("upgrade", {
        edition: data.edition, fullFirmwareLink: data.fullFirmwareLink, type
      }, {type: 5, fail});

      if (type === 2 && result.data.status !== 1) {
        switch (result.data.status) {
          case 2:
            throw WebError.code(deviceError.FIRMWARE_UPGRADE_ERROR, {msg: "发起镜像升级失败，内存不足"})
          case 3:
            throw WebError.code(deviceError.FIRMWARE_UPGRADE_ERROR, {msg: "发起镜像升级失败，请连接wifi后使用"})
          case 4:
            throw WebError.code(deviceError.FIRMWARE_UPGRADE_ERROR, {msg: "发起镜像升级失败，电池需保持在80%以上"})
        }
      }
    } catch (e) {
      let desc = "升级失败";

      if (deviceError.isError(e)) {
        desc = e.msg;
      }

      this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {
        step: -1, progress: 0, desc: desc
      });

      throw e;
    }

    const clear = {};
    let clearFn;

    let deviceReconnect = () => {

    };

    if (type === 1) {
      deviceReconnect = new Promise((resolve, reject) => {
        this.send.listen("progress", true, {
          cacheTopic: clear, type: 5, success, complete, message: async (msg) => {
            progress.step = msg.params.step;
            progress.progress = 0;
            progress.desc = msg.params.desc;

            message?.call(this, msg);
            this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, progress);

            if (msg.params.step === 3) {
              resolve();
              await reconnect();
            }
          }, fail, timeout
        });

        const reconnect = async () => {
          await this.deviceProtocol.disconnect({
            logout: true,
            type: 3, connectType: 2
          });
          await this.deviceProtocol.connect();
        }

        const listenProperty = async msg => {
          if (msg.version === data.edition) {
            progress.step = 3
            progress.progress = 100
            progress.desc = '升级成功'
            this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, progress);
            resolve();
            await reconnect();
          }
        };

        clearFn = () => {
          this.send.unsubscribe(clear);
          this.deviceProtocol.removeListener(DeviceEvent.PROPERTY, listenProperty);
        }

        this.deviceProtocol.on(DeviceEvent.PROPERTY, listenProperty);
      }).catch(e => {
        this.deviceProtocol.emit(DeviceEvent.OTA_PROGRESS, {
          step: -1, progress: 0, desc: "升级失败"
        });
        throw e;
      }).finally(() => {
        clearFn?.call(this);
      })
    }

    return {
      autoReconnect: true,
      reconnect: () => {
        return deviceReconnect;
      }
    }
  }

  async _singing({state, success, complete, message, fail, timeout} = {}) {
    return this.send.service("singing", {state}, {success, complete, message, fail, timeout});
  }

  async _wifiSetting({type, save = 1, name, password, success, complete, message, fail, timeout}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }

  async _wifiList({onWifi, success, complete, message, fail, timeout}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT);
  }

  async _envChange({api, iot, type, clear, success, complete, message, fail, timeout} = {}) {
    return this.send.service("env_change", {api, iot, type, clear}, {success, complete, message, fail, timeout});
  }

  async _connectChange({usbMode, success, complete, message, fail, timeout} = {}) {
    throw WebError.code(deviceError.PROTOCOL_NOT_SUPPORT)
  }

  async release() {
    return this.send.unsubscribe();
  }
}

export {
  MqttDeviceConnectProvide,
  MqttDeviceProtocolBeginSeal,
  MqttDeviceProtocol,
  MqttDeviceProtocolOperation,
  MqttSend
};